// Tension Mapper — React port of kula-create's tension-mapper.html using
// @dnd-kit/core. Factors drag along a continuum between two poles; submit
// reveals the expert analysis tied to each factor.
//
// Single-row capture on submit:
//   { placements: Array<{ factorId, position: 0..100 }> }.

import { useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { TensionMapperSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';
import { ObsidianTensionMapper } from './ObsidianTensionMapper';

interface Props {
  data: TensionMapperSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: TensionMapperSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function TensionMapperSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  // Tier-A bespoke renderer for the "Obsidian Dossier" world (home-set pairing #2).
  if (theme.id === 'obsidian') {
    return <ObsidianTensionMapper data={data} theme={theme} onActivityResponse={onActivityResponse} onComplete={onComplete} savedResponse={savedResponse} />;
  }
  return (
    <Student
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

/**
 * Parse a saved response into a positions map. Accepts the canonical
 * { placements: [{ factorId, position }] } shape, plus a legacy mangled
 * shape ({ questionId: 'placements', text: [...] }) produced by a pipeline
 * bug that's being fixed separately.
 */
function parseSavedPositions(saved: Record<string, unknown> | null | undefined): Record<string, number> | null {
  if (!saved) return null;
  const raw = Array.isArray(saved.placements)
    ? saved.placements
    : saved.questionId === 'placements' && Array.isArray(saved.text)
      ? saved.text
      : null;
  if (!raw) return null;
  const positions: Record<string, number> = {};
  for (const item of raw) {
    if (item && typeof item === 'object') {
      const { factorId, position } = item as { factorId?: unknown; position?: unknown };
      if (typeof factorId === 'string' && typeof position === 'number') positions[factorId] = position;
    }
  }
  return Object.keys(positions).length > 0 ? positions : null;
}

function Student({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: TensionMapperSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  // A saved {placements} payload is the end state — restore the submitted
  // view (placed chips + expert analysis). Cheap to recompute; only the
  // initial-state values are used.
  const restored = parseSavedPositions(savedResponse);
  // factorId → position 0..100 (50 = centre); undefined = still in pool
  const [positions, setPositions] = useState<Record<string, number>>(restored ?? {});
  const [submitted, setSubmitted] = useState(restored !== null);
  const completedRef = useRef(restored !== null);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const placedCount = Object.keys(positions).length;
  const allPlaced = placedCount === data.factors.length && data.factors.length > 0;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (submitted) return;
      if (!event.over || event.over.id !== 'continuum') return;
      const factorId = String(event.active.id);
      // The continuum droppable reports `over.rect` and we use the delta to
      // compute a normalised x-position. We capture coordinates via the
      // active rect's final position.
      const rect = event.over.rect;
      const activeRect = event.active.rect.current.translated;
      if (!rect || !activeRect) return;
      const itemCentreX = activeRect.left + activeRect.width / 2;
      const pct = ((itemCentreX - rect.left) / rect.width) * 100;
      const clamped = Math.max(0, Math.min(100, pct));
      setPositions((prev) => ({ ...prev, [factorId]: clamped }));
    },
    [submitted],
  );

  const submit = useCallback(() => {
    setSubmitted(true);
    onActivityResponse?.({
      activityType: 'tension-mapper',
      mode: 'survey',
      responseData: {
        placements: data.factors
          .filter((f) => positions[f.id] !== undefined)
          .map((f) => ({ factorId: f.id, position: positions[f.id] })),
      },
    });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [data.factors, positions, onActivityResponse, onComplete]);

  const removeFactor = useCallback(
    (factorId: string) => {
      if (submitted) return;
      setPositions((prev) => {
        const next = { ...prev };
        delete next[factorId];
        return next;
      });
    },
    [submitted],
  );

  // Close reflect-back: the factor the learner committed most strongly (furthest
  // from the 50 centre) and which pole they leaned (drives Tier-3 variants).
  const placed = data.factors
    .filter((f) => positions[f.id] !== undefined)
    .map((f) => ({ f, pos: positions[f.id] }));
  const avgPos = placed.length ? placed.reduce((s, p) => s + p.pos, 0) / placed.length : undefined;
  const outcome =
    avgPos === undefined ? undefined : avgPos < 45 ? 'poleA' : avgPos > 55 ? 'poleB' : 'balanced';
  const topFactor = placed.length
    ? placed.reduce((a, b) => (Math.abs(b.pos - 50) > Math.abs(a.pos - 50) ? b : a)).f.label
    : undefined;
  const conclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'So what',
    body: 'The factor you weighted most heavily was {topFactor}.',
    reflectionPrompt: data.reflection?.prompt,
  };

  const poolFactors = data.factors.filter((f) => positions[f.id] === undefined);
  const placedFactors = data.factors.filter((f) => positions[f.id] !== undefined);

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[820px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
        </header>

        {/* Tension statement */}
        <div className="rounded-lg p-5 mb-6 border-l-4" style={{ background: theme.colors.surface, borderColor: accent }}>
          <p className="text-sm font-semibold mb-3" style={{ color: theme.colors.text }}>
            {data.tension.statement}
          </p>
          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            <div className="rounded p-3" style={{ background: theme.colors.background }}>
              <p className="font-bold mb-1" style={{ color: accent }}>← {data.tension.poleA.label}</p>
              <p style={{ color: theme.colors.textMuted }}>{data.tension.poleA.description}</p>
            </div>
            <div className="rounded p-3 text-right" style={{ background: theme.colors.background }}>
              <p className="font-bold mb-1" style={{ color: accent }}>{data.tension.poleB.label} →</p>
              <p style={{ color: theme.colors.textMuted }}>{data.tension.poleB.description}</p>
            </div>
          </div>
        </div>

        {data.factors.length === 0 ? (
          <div
            className="rounded-lg p-6 text-center border"
            style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
          >
            <p className="text-sm" style={{ color: theme.colors.textMuted }}>
              This tension has no factors yet — regenerate it via the AI build to add content.
            </p>
          </div>
        ) : (
          <>
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              {/* Factor pool */}
              {poolFactors.length > 0 && !submitted && (
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                    Drag each factor onto the continuum
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {poolFactors.map((factor) => (
                      <FactorChip key={factor.id} factor={factor} theme={theme} accent={accent} disabled={false} />
                    ))}
                  </div>
                </div>
              )}

              {/* Continuum */}
              <ContinuumZone
                poleA={data.tension.poleA.label}
                poleB={data.tension.poleB.label}
                theme={theme}
                accent={accent}
              >
                {placedFactors.map((factor, placedIdx) => (
                  <PlacedFactor
                    key={factor.id}
                    factor={factor}
                    position={positions[factor.id]}
                    verticalSlot={placedIdx % 3}
                    theme={theme}
                    accent={accent}
                    disabled={submitted}
                    onRemove={submitted ? undefined : () => removeFactor(factor.id)}
                  />
                ))}
              </ContinuumZone>
            </DndContext>

            {!submitted && allPlaced && (
              <button
                onClick={submit}
                className="w-full mt-4 px-6 py-3 rounded-lg font-semibold text-sm"
                style={{ background: accent, color: '#fff' }}
              >
                Lock my mapping
              </button>
            )}
          </>
        )}

        <AnimatePresence>
          {submitted && (
            <Reveal variant="up" className="mt-6 space-y-3">
              {/* Payoff register: heavier accent rule + larger radius than the setup card */}
              <div className="rounded-xl p-5 border-l-[5px] shadow-sm" style={{ background: theme.colors.surface, borderColor: accent }}>
                <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                  Expert analysis
                </p>
                <p className="text-sm leading-relaxed mb-3" style={{ color: theme.colors.text }}>
                  {data.expertAnalysis.summary}
                </p>
                <ul className="space-y-2 text-sm">
                  {data.expertAnalysis.insights.map((ins, i) => {
                    const factor = data.factors.find((f) => f.id === ins.factorId);
                    return (
                      <li key={i} className="ml-4 list-disc" style={{ color: theme.colors.text }}>
                        <strong>{factor?.label ?? ins.factorId}:</strong> {ins.perspective}
                      </li>
                    );
                  })}
                </ul>
              </div>
              {/* Authored "So what?" close — replaces the old bare Reflect prompt
                  (no input). Names the factor they committed most to and branches
                  on the pole they leaned; folds the legacy prompt into reflection. */}
              <ExperienceClose
                conclusion={conclusion}
                theme={theme}
                values={{ topFactor }}
                outcome={outcome}
                fallback="Look back at where you placed each factor between the two poles."
              />
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function ContinuumZone({
  poleA,
  poleB,
  theme,
  accent,
  children,
}: {
  poleA: string;
  poleB: string;
  theme: Theme;
  accent: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: 'continuum' });
  return (
    <div
      ref={setNodeRef}
      className="relative rounded-lg p-4 border-2"
      style={{
        background: theme.colors.surface,
        borderColor: isOver ? accent : `${accent}30`,
        borderStyle: 'dashed',
        minHeight: 220,
      }}
    >
      {/* Line + poles */}
      <div className="relative h-1 my-12 rounded-full" style={{ background: `${accent}40` }} />
      <div className="absolute left-4 top-2 text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>
        ← {poleA}
      </div>
      <div className="absolute right-4 top-2 text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>
        {poleB} →
      </div>
      {children}
    </div>
  );
}

function FactorChip({
  factor,
  theme,
  accent,
  disabled,
}: {
  factor: TensionMapperSlideData['factors'][number];
  theme: Theme;
  accent: string;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: factor.id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="px-3 py-2 rounded border-2 text-sm font-medium"
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        background: theme.colors.background,
        borderColor: accent,
        color: theme.colors.text,
        opacity: isDragging ? 0.4 : 1,
        cursor: disabled ? 'default' : 'grab',
        maxWidth: 220,
      }}
      title={factor.description}
    >
      {factor.label}
    </div>
  );
}

// Cycled vertical offsets so chips placed near the same x stay legible.
const PLACED_SLOT_TOPS = ['25%', '48%', '71%'];

function PlacedFactor({
  factor,
  position,
  verticalSlot,
  theme,
  accent,
  disabled,
  onRemove,
}: {
  factor: TensionMapperSlideData['factors'][number];
  position: number;
  verticalSlot: number;
  theme: Theme;
  accent: string;
  disabled: boolean;
  onRemove?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: factor.id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="absolute px-2 py-1 rounded border-2 text-xs font-medium flex items-center gap-1"
      style={{
        left: `${position}%`,
        top: PLACED_SLOT_TOPS[verticalSlot % PLACED_SLOT_TOPS.length],
        transform: transform
          ? `translate(-50%, 0) translate3d(${transform.x}px, ${transform.y}px, 0)`
          : 'translate(-50%, 0)',
        background: theme.colors.background,
        borderColor: accent,
        color: theme.colors.text,
        opacity: isDragging ? 0.4 : 1,
        cursor: disabled ? 'default' : 'grab',
        maxWidth: 150,
      }}
      title={factor.description}
    >
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{factor.label}</span>
      {onRemove && (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="font-bold leading-none"
          style={{ color: accent, cursor: 'pointer' }}
          aria-label={`Return ${factor.label} to the pool`}
        >
          ×
        </button>
      )}
    </div>
  );
}

function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: TensionMapperSlideData;
  theme: Theme;
  onUpdate: (data: TensionMapperSlideData) => void;
}) {
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px] space-y-3">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <textarea
          value={data.tension.statement}
          onChange={(e) => onUpdate({ ...data, tension: { ...data.tension, statement: e.target.value } })}
          placeholder="Tension statement"
          rows={2}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            value={data.tension.poleA.label}
            onChange={(e) => onUpdate({ ...data, tension: { ...data.tension, poleA: { ...data.tension.poleA, label: e.target.value } } })}
            placeholder="Pole A label"
            className="px-2 py-1 rounded border text-sm"
            style={{
              background: theme.colors.surface,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
          <input
            value={data.tension.poleB.label}
            onChange={(e) => onUpdate({ ...data, tension: { ...data.tension, poleB: { ...data.tension.poleB, label: e.target.value } } })}
            placeholder="Pole B label"
            className="px-2 py-1 rounded border text-sm"
            style={{
              background: theme.colors.surface,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
        </div>
        <div className="rounded p-3 text-xs" style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}>
          {data.factors.length} factor{data.factors.length === 1 ? '' : 's'} · {data.expertAnalysis.insights.length} expert insight(s).
          Structural edits AI-only — regenerate via AI build.
        </div>
      </div>
    </div>
  );
}
